import React from 'react';
import { Mail, User, CheckCircle, AlertCircle } from 'lucide-react';
import PhoneInput from './PhoneInput';

export interface PersonalDataValues {
  email: string;
  confirm_email?: string;
  first_name: string;
  last_name: string;
  phone: string;
}

export type PersonalDataField = keyof PersonalDataValues;
export type PersonalDataErrors = Partial<Record<PersonalDataField, string>>;

export const isValidEmailFormat = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const isValidPhoneFormat = (phone: string): boolean =>
  /^\+\d{1,4}\d{7,}$/.test(phone);

/** Shared validation for the signup forms; returns an empty object when valid. */
export function validatePersonalData(
  values: PersonalDataValues,
  { withEmailConfirmation = false }: { withEmailConfirmation?: boolean } = {}
): PersonalDataErrors {
  const errors: PersonalDataErrors = {};
  if (!values.email.trim()) errors.email = 'Email es requerido';
  else if (!isValidEmailFormat(values.email)) errors.email = 'Email no válido';
  if (withEmailConfirmation && values.email.trim().toLowerCase() !== (values.confirm_email ?? '').trim().toLowerCase()) {
    errors.confirm_email = 'Los correos no coinciden';
  }
  if (!values.first_name.trim()) errors.first_name = 'Nombre es requerido';
  if (!values.last_name.trim()) errors.last_name = 'Apellido es requerido';
  if (!values.phone.trim()) errors.phone = 'Teléfono es requerido';
  else if (!isValidPhoneFormat(values.phone)) errors.phone = 'Formato de teléfono inválido';
  return errors;
}

interface PersonalDataFieldsProps {
  values: PersonalDataValues;
  errors: PersonalDataErrors;
  onChange: (field: PersonalDataField, value: string) => void;
  disabled?: boolean;
  withEmailConfirmation?: boolean;
  phoneCountry?: string;
}

const fieldClass = (hasError: boolean) =>
  `w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-all duration-200 ${
    hasError ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#1CB3B3] focus:bg-[#E8F7F7]'
  }`;

const PersonalDataFields: React.FC<PersonalDataFieldsProps> = ({
  values,
  errors,
  onChange,
  disabled = false,
  withEmailConfirmation = false,
  phoneCountry = 'CO',
}) => {
  const isValidEmail = isValidEmailFormat(values.email);
  const confirmMatches =
    !!values.confirm_email && values.confirm_email.trim().toLowerCase() === values.email.trim().toLowerCase();

  return (
    <>
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="email"
            id="signup-email"
            value={values.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="tu@email.com"
            autoComplete="email"
            className={`w-full pl-10 pr-10 py-3 border-2 rounded-lg focus:outline-none transition-all duration-200 ${
              errors.email ? 'border-red-300 bg-red-50'
                : values.email && isValidEmail ? 'border-[#1CB3B3] bg-[#E8F7F7]'
                : values.email && !isValidEmail ? 'border-red-300 bg-red-50'
                : 'border-gray-200 focus:border-[#1CB3B3] focus:bg-[#E8F7F7]'
            }`}
            disabled={disabled}
          />
          {values.email && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {isValidEmail
                ? <CheckCircle className="h-5 w-5 text-[#1CB3B3]" />
                : <AlertCircle className="h-5 w-5 text-red-500" />}
            </div>
          )}
        </div>
        {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
      </div>

      {withEmailConfirmation && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar email *</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="signup-confirm-email"
              value={values.confirm_email ?? ''}
              onChange={(e) => onChange('confirm_email', e.target.value)}
              onPaste={(e) => e.preventDefault()}
              placeholder="Escribe de nuevo tu email"
              autoComplete="off"
              className={`${fieldClass(!!errors.confirm_email)} pr-10`}
              disabled={disabled}
            />
            {values.confirm_email && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {confirmMatches
                  ? <CheckCircle className="h-5 w-5 text-[#1CB3B3]" />
                  : <AlertCircle className="h-5 w-5 text-red-500" />}
              </div>
            )}
          </div>
          {errors.confirm_email && <p className="text-red-600 text-xs mt-1">{errors.confirm_email}</p>}
        </div>
      )}

      {/* Name fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="signup-first-name"
              value={values.first_name}
              onChange={(e) => onChange('first_name', e.target.value)}
              placeholder="Carlos"
              autoComplete="given-name"
              className={fieldClass(!!errors.first_name)}
              disabled={disabled}
            />
          </div>
          {errors.first_name && <p className="text-red-600 text-xs mt-1">{errors.first_name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Apellido *</label>
          <input
            type="text"
            id="signup-last-name"
            value={values.last_name}
            onChange={(e) => onChange('last_name', e.target.value)}
            placeholder="Martínez"
            autoComplete="family-name"
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-all duration-200 ${
              errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-[#1CB3B3] focus:bg-[#E8F7F7]'
            }`}
            disabled={disabled}
          />
          {errors.last_name && <p className="text-red-600 text-xs mt-1">{errors.last_name}</p>}
        </div>
      </div>

      {/* Phone */}
      <PhoneInput
        value={values.phone}
        onChange={(value) => onChange('phone', value)}
        error={errors.phone}
        disabled={disabled}
        defaultCountry={phoneCountry}
      />
    </>
  );
};

export default PersonalDataFields;
